import * as XLSX from 'xlsx-js-style';

const REQUIRED_HEADER_STYLE = {
    fill: { patternType: 'solid', fgColor: { rgb: 'FFF2CC' } },
    font: { bold: true, color: { rgb: '3F2E00' } },
    alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
};

const OPTIONAL_HEADER_STYLE = {
    fill: { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } },
    font: { bold: true, color: { rgb: '4B5563' } },
    alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
};

const GUIDE_HEADER_STYLE = {
    fill: { patternType: 'solid', fgColor: { rgb: 'E5E7EB' } },
    font: { bold: true, color: { rgb: '111827' } },
    alignment: { vertical: 'center', wrapText: true },
};

type TemplateColumn = {
    label: string;
    width: number;
};

const templateColumns: TemplateColumn[] = [
    { label: 'Internal ID [MANDATORY]', width: 20 },
    { label: 'Product Name [MANDATORY]', width: 42 },
    { label: 'Brand [MANDATORY]', width: 20 },
    { label: 'Category [MANDATORY]', width: 26 },
    { label: 'Subcategory [MANDATORY]', width: 26 },
    { label: 'Product Type [MANDATORY]', width: 26 },
    { label: 'Marketplace [MANDATORY]', width: 18 },
    { label: 'Language [MANDATORY]', width: 18 },
    { label: 'Brand Website [OPTIONAL]', width: 28 },
    { label: 'Variation - Size [OPTIONAL]', width: 22 },
    { label: 'Variation - Color [OPTIONAL]', width: 22 },
    { label: 'Variation - Pack Quantity [OPTIONAL]', width: 24 },
    { label: 'Variation - ... [OPTIONAL]', width: 22 },
    { label: 'Product Contents [MANDATORY]', width: 32 },
    { label: 'Unique Selling Proposition / USP [MANDATORY]', width: 38 },
    { label: 'Key Benefit - 1 [MANDATORY]', width: 34 },
    { label: 'Key Benefit - 2 [MANDATORY]', width: 34 },
    { label: 'Key Benefit - 3 [MANDATORY]', width: 34 },
    { label: 'Key Benefit - 4 [OPTIONAL]', width: 34 },
    { label: 'Key Benefit - 5 [OPTIONAL]', width: 34 },
    { label: 'Global Must-Have Keywords [OPTIONAL]', width: 34 },
    { label: 'Title Keywords - High Priority [OPTIONAL]', width: 34 },
    { label: 'Title Keywords - Optional [OPTIONAL]', width: 34 },
    { label: 'Bullet Point Keywords - High Priority [OPTIONAL]', width: 36 },
    { label: 'Bullet Point Keywords - Optional [OPTIONAL]', width: 36 },
    { label: 'Description Keywords - High Priority [OPTIONAL]', width: 38 },
    { label: 'Description Keywords - Optional [OPTIONAL]', width: 36 },
    { label: 'Compliance [OPTIONAL]', width: 30 },
    { label: 'Competitors [OPTIONAL]', width: 28 },
    { label: 'Technical Specs - Dimensions [OPTIONAL]', width: 34 },
    { label: 'Technical Specs - Weight [OPTIONAL]', width: 30 },
    { label: 'Technical Specs - Material [OPTIONAL]', width: 30 },
    { label: 'Technical Specs - ... [OPTIONAL]', width: 30 },
    { label: 'Negative Keywords [OPTIONAL]', width: 34 },
    { label: 'Target Audience [OPTIONAL]', width: 32 },
    { label: 'Brand Description [OPTIONAL]', width: 36 },
];

const columnGuideRows = [
    ['Column', 'Guidance', 'Example'],
    ['Mandatory columns', 'Yellow headers must be filled before upload.', ''],
    ['Internal ID', 'Your SKU, EAN, ASIN, or other stable product identifier.', '50851N075'],
    ['Category', 'Broad product family. Used as supporting context for keyword research and assignment.', 'Koerperpflege'],
    ['Subcategory', 'Narrower grouping between category and product type.', 'Koerpercreme & Koerperbutter'],
    ['Product Type', 'Exact product class. This is the strongest taxonomy signal for keyword research.', 'Koerperbutter'],
    ['Marketplace', 'European marketplace or country code.', 'DE'],
    ['Language', 'ISO language code. Use codes such as de, en, fr, es, it, nl, pl, sv.', 'de'],
    ['Keyword columns', 'Add multiple keywords separated by commas.', 'bodybutter, koerperbutter, trockene haut'],
    ['Variation / Technical Specs', 'Duplicate the pattern with another suffix if you need more columns.', 'Technical Specs - Certification [OPTIONAL]'],
];

const isMandatoryHeader = (header: string) => header.toLowerCase().includes('[mandatory]');

export const downloadCustomProductInputTemplate = () => {
    const headers = [templateColumns.map((column) => column.label)];

    const worksheet = XLSX.utils.aoa_to_sheet(headers) as XLSX.WorkSheet & {
        '!freeze'?: Record<string, unknown>;
    };

    headers[0].forEach((header, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
        const cell = worksheet[cellAddress] as XLSX.CellObject & {
            s?: Record<string, unknown>;
        };
        if (!cell) return;
        cell.s = isMandatoryHeader(header) ? REQUIRED_HEADER_STYLE : OPTIONAL_HEADER_STYLE;
    });

    worksheet['!cols'] = templateColumns.map((column) => ({ wch: column.width }));
    worksheet['!rows'] = [{ hpt: 38 }];
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
    worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: 0, c: headers[0].length - 1 },
        }),
    };

    const guideWorksheet = XLSX.utils.aoa_to_sheet(columnGuideRows);
    columnGuideRows[0].forEach((_, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
        const cell = guideWorksheet[cellAddress] as XLSX.CellObject & {
            s?: Record<string, unknown>;
        };
        if (!cell) return;
        cell.s = GUIDE_HEADER_STYLE;
    });
    guideWorksheet['!cols'] = [{ wch: 26 }, { wch: 78 }, { wch: 44 }];
    guideWorksheet['!rows'] = [{ hpt: 26 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.utils.book_append_sheet(workbook, guideWorksheet, 'Column Guide');

    XLSX.writeFile(workbook, 'custom_product_template.xlsx');
};
